import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { AddressAutocomplete } from "@/components/properties/AddressAutocomplete";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { User, MapPin, Home, Target, Loader2, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Step 1 schema - required fields only
const step1Schema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().regex(/^\+1\d{10}$/, "Please enter a valid 10-digit phone number"),
  address: z.string().min(5, "Please enter your street address"),
  city: z.string().min(2, "Please enter your city"),
  state: z.string().min(2, "Please select your state"),
  zipCode: z.string().min(5, "Please enter a valid ZIP code"),
});

// Full schema including optional Step 2 fields
const fullFormSchema = step1Schema.extend({
  garageType: z.string().optional(),
  basementType: z.string().optional(),
  ownershipDuration: z.string().optional(),
  condition: z.string().optional(),
  repairsNeeded: z.string().optional(),
  occupancy: z.string().optional(),
  listedWithRealtor: z.string().optional(),
  needToSellFast: z.string().optional(),
  goal: z.string().optional(),
  bestTimeToCall: z.string().optional(),
  smsConsent: z.boolean().default(false),
});

type FormData = z.infer<typeof fullFormSchema>;

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const StepIndicator = ({ currentStep }: { currentStep: 1 | 2 }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-foreground">
        Step {currentStep} of 2
      </span>
      <span className="text-sm text-muted-foreground">
        {currentStep === 1 ? "Basic Info" : "Additional Details"}
      </span>
    </div>
    <Progress value={currentStep === 1 ? 50 : 100} className="h-2" />
    <div className="flex justify-between mt-2">
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
          currentStep >= 1 ? "bg-godyspo-gold text-black" : "bg-muted text-muted-foreground"
        }`}>
          1
        </div>
        <span className={`text-xs ${currentStep >= 1 ? "text-foreground" : "text-muted-foreground"}`}>
          Contact & Address
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
          currentStep >= 2 ? "bg-godyspo-gold text-black" : "bg-muted text-muted-foreground"
        }`}>
          2
        </div>
        <span className={`text-xs ${currentStep >= 2 ? "text-foreground" : "text-muted-foreground"}`}>
          Property Details
        </span>
      </div>
    </div>
  </div>
);

const GetOfferForm = () => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingStep1, setIsSubmittingStep1] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [leadId, setLeadId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(fullFormSchema),
    defaultValues: {
      smsConsent: false,
    },
  });

  const handleAddressSelect = (addressData: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  }) => {
    setValue("address", addressData.address);
    setValue("city", addressData.city);
    setValue("state", addressData.state);
    setValue("zipCode", addressData.zipCode);
  };

  const handleContinue = async () => {
    // Validate only step 1 fields
    const isValid = await trigger(["fullName", "email", "phone", "address", "city", "state", "zipCode"]);
    if (!isValid) return;

    setIsSubmittingStep1(true);
    
    try {
      // Submit Step 1 data to capture partial lead
      const step1Data = {
        fullName: getValues("fullName"),
        email: getValues("email"),
        phone: getValues("phone"),
        address: getValues("address"),
        city: getValues("city"),
        state: getValues("state"),
        zipCode: getValues("zipCode"),
      };

      const { data: response, error } = await supabase.functions.invoke("submit-offer-step1", {
        body: step1Data,
      });

      if (!error && response?.leadId) {
        setLeadId(response.leadId);
        console.log("Step 1 lead captured:", response.leadId);
      } else {
        // Log error but don't block user from proceeding
        console.error("Step 1 submission error (non-blocking):", error);
      }
    } catch (err) {
      // Non-blocking: allow user to continue even if Step 1 API fails
      console.error("Step 1 API call failed (non-blocking):", err);
    } finally {
      setIsSubmittingStep1(false);
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      // Include leadId to link Step 2 with Step 1 submission
      const { error } = await supabase.functions.invoke("submit-offer-request", {
        body: {
          ...data,
          leadId: leadId, // Links to partial lead from Step 1
        },
      });

      if (error) throw error;

      setIsSuccess(true);
      toast.success("Request submitted successfully!");
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast.error(error.message || "Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Card className="bg-card border-godyspo-gold/30">
        <CardContent className="pt-12 pb-12 text-center">
          <div className="w-16 h-16 bg-godyspo-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8 text-godyspo-gold" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">
            Thank You!
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            We've received your request and will be in touch soon with a no-obligation cash offer for your property.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <StepIndicator currentStep={currentStep} />

      {currentStep === 1 && (
        <form onSubmit={(e) => { e.preventDefault(); handleContinue(); }} className="space-y-6">
          {/* Contact Information */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                <User className="h-5 w-5 text-godyspo-gold" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  {...register("fullName")}
                  placeholder="John Smith"
                />
                {errors.fullName && (
                  <p className="text-sm text-destructive">{errors.fullName.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    {...register("email")}
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <PhoneInput
                    value={watch("phone") || ""}
                    onChange={(value) => setValue("phone", value)}
                    placeholder="(555) 123-4567"
                    error={errors.phone?.message}
                    showValidation
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Property Address */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                <MapPin className="h-5 w-5 text-godyspo-gold" />
                Property Address
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Street Address *</Label>
                <AddressAutocomplete
                  initialAddress={watch("address") || ""}
                  onAddressSelect={handleAddressSelect}
                  onInputChange={(value) => setValue("address", value)}
                  showLabel={false}
                />
                {errors.address && (
                  <p className="text-sm text-destructive">{errors.address.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    {...register("city")}
                    placeholder="City"
                  />
                  {errors.city && (
                    <p className="text-sm text-destructive">{errors.city.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Select onValueChange={(value) => setValue("state", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.state && (
                    <p className="text-sm text-destructive">{errors.state.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zipCode">ZIP Code *</Label>
                  <Input
                    id="zipCode"
                    {...register("zipCode")}
                    placeholder="12345"
                  />
                  {errors.zipCode && (
                    <p className="text-sm text-destructive">{errors.zipCode.message}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Continue Button */}
          <Button
            type="submit"
            disabled={isSubmittingStep1}
            className="w-full bg-godyspo-gold hover:bg-godyspo-gold/90 text-black font-semibold py-6 text-lg"
          >
            {isSubmittingStep1 ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Next: Tell us more about your property (optional)
          </p>
        </form>
      )}

      {currentStep === 2 && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Property Information */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                <Home className="h-5 w-5 text-godyspo-gold" />
                Property Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Garage Type</Label>
                  <Select onValueChange={(value) => setValue("garageType", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select garage type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="1-car-attached">1 Car Attached</SelectItem>
                      <SelectItem value="2-car-attached">2 Car Attached</SelectItem>
                      <SelectItem value="1-car-detached">1 Car Detached</SelectItem>
                      <SelectItem value="2-car-detached">2 Car Detached</SelectItem>
                      <SelectItem value="3-plus-car">3+ Car</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Foundation Type</Label>
                  <Select onValueChange={(value) => setValue("basementType", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select foundation type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basement-unfinished">Basement-Unfinished</SelectItem>
                      <SelectItem value="basement-partially-finished">Basement-Partially Finished</SelectItem>
                      <SelectItem value="basement-fully-finished">Basement-Fully Finished</SelectItem>
                      <SelectItem value="partial-basement">Partial Basement</SelectItem>
                      <SelectItem value="crawlspace">Crawlspace</SelectItem>
                      <SelectItem value="slab">Slab</SelectItem>
                      <SelectItem value="pier-and-beam">Pier and Beam/Pile</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>How long have you owned the property?</Label>
                  <Select onValueChange={(value) => setValue("ownershipDuration", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="less-than-1-year">Less than 1 year</SelectItem>
                      <SelectItem value="1-3-years">1-3 years</SelectItem>
                      <SelectItem value="3-5-years">3-5 years</SelectItem>
                      <SelectItem value="5-10-years">5-10 years</SelectItem>
                      <SelectItem value="10-plus-years">10+ years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Current condition of the property?</Label>
                  <Select onValueChange={(value) => setValue("condition", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                      <SelectItem value="needs-major-repairs">Needs Major Repairs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="repairsNeeded">What kind of repairs/maintenance does the house need?</Label>
                <Textarea
                  id="repairsNeeded"
                  {...register("repairsNeeded")}
                  placeholder="Describe any repairs or maintenance needed..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Is there anyone living in the house?</Label>
                  <Select onValueChange={(value) => setValue("occupancy", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select occupancy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner-occupied">Yes - Owner Occupied</SelectItem>
                      <SelectItem value="tenant-occupied">Yes - Tenant Occupied</SelectItem>
                      <SelectItem value="vacant">No - Vacant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Is the house currently listed with a realtor?</Label>
                  <Select onValueChange={(value) => setValue("listedWithRealtor", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Your Situation */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                <Target className="h-5 w-5 text-godyspo-gold" />
                Your Situation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Do you need to sell your house fast?</Label>
                  <Select onValueChange={(value) => setValue("needToSellFast", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="maybe">Maybe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Best time to call</Label>
                  <Select onValueChange={(value) => setValue("bestTimeToCall", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning</SelectItem>
                      <SelectItem value="afternoon">Afternoon</SelectItem>
                      <SelectItem value="evening">Evening</SelectItem>
                      <SelectItem value="anytime">Anytime</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>What's your ultimate goal with your house?</Label>
                <Select onValueChange={(value) => setValue("goal", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sell-for-cash">Sell for cash</SelectItem>
                    <SelectItem value="sell-for-down-payment">Sell for down payment on new home</SelectItem>
                    <SelectItem value="avoid-foreclosure">Avoid foreclosure</SelectItem>
                    <SelectItem value="relocating">Relocating</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-start space-x-3 pt-2">
                <Checkbox
                  id="smsConsent"
                  checked={watch("smsConsent")}
                  onCheckedChange={(checked) => setValue("smsConsent", checked as boolean)}
                />
                <Label htmlFor="smsConsent" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                  I consent to receive SMS text messages regarding my property inquiry. Message and data rates may apply. Reply STOP to opt out.
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              className="sm:flex-1 py-6"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="sm:flex-[2] bg-godyspo-gold hover:bg-godyspo-gold/90 text-black font-semibold py-6 text-lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Get My Cash Offer"
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            By submitting this form, you agree to our{" "}
            <a href="/privacy-policy" className="text-godyspo-gold hover:underline">
              Privacy Policy
            </a>{" "}
            and{" "}
            <a href="/terms-and-conditions" className="text-godyspo-gold hover:underline">
              Terms of Service
            </a>.
          </p>
        </form>
      )}
    </div>
  );
};

export default GetOfferForm;
